import React from 'react';
import { ShieldCheck, ShieldAlert } from 'lucide-react';

/**
 * Diagonal corner ribbon shown on directory company cards & detail pages.
 *
 * Reflects verification state:
 *   - verified: true  -> "Trados Verified" (+ tier), tier-colored
 *   - verified: false -> "Not Yet Trados Verified", muted gray
 *
 * IMPORTANT: The PARENT element must be `position: relative` and `overflow-hidden`
 * so the diagonal banner clips cleanly to the corner without shifting layout.
 *
 * Props:
 *   - verification: { verified: bool, label: string, tier: 'bronze'|'silver'|'gold'|null }
 *   - size: 'sm' | 'md' (md is larger, for the detail page)
 */
const TIER_COLORS = {
  bronze: '#B87333',
  silver: '#8A9BA8',
  gold: '#C9A227',
};

const TradosVerifiedRibbon = ({ verification, size = 'sm' }) => {
  const verified = !!verification?.verified;
  const tier = verification?.tier;

  const bg = verified ? (TIER_COLORS[tier] || '#2B4B6A') : '#9CA3AF';
  const tierLabel = tier ? ` · ${tier.charAt(0).toUpperCase()}${tier.slice(1)}` : '';
  const label = verified ? `Trados Verified${tierLabel}` : 'Not Yet Trados Verified';

  // Geometry of the clip box + rotated banner.
  // The visible diagonal chord must exceed the label length: with the banner
  // center sitting on the corner diagonal, chord ≈ 2 × (distance from corner),
  // so box/banner are sized generously for the longest label
  // ("Not Yet Trados Verified" ≈ 150px at 10px uppercase).
  const box = size === 'md' ? 170 : 136;
  const bannerWidth = size === 'md' ? 260 : 210;
  const offset = size === 'md' ? -64 : -52;
  const top = size === 'md' ? 46 : 36;
  const fontSize = size === 'md' ? 10 : 8.5;

  return (
    <div
      className="absolute top-0 right-0 overflow-hidden pointer-events-none z-10"
      style={{ width: box, height: box }}
      aria-label={label}
    >
      <div
        className="absolute flex items-center justify-center gap-1 font-semibold uppercase tracking-wide text-center shadow-md"
        style={{
          backgroundColor: bg,
          color: '#FFFFFF',
          width: bannerWidth,
          right: offset,
          top,
          transform: 'rotate(45deg)',
          paddingTop: 3,
          paddingBottom: 3,
          fontSize,
          letterSpacing: '0.03em',
          whiteSpace: 'nowrap',
        }}
      >
        {verified ? (
          <ShieldCheck style={{ width: fontSize + 2, height: fontSize + 2 }} />
        ) : (
          <ShieldAlert style={{ width: fontSize + 2, height: fontSize + 2 }} />
        )}
        {label}
      </div>
    </div>
  );
};

export default TradosVerifiedRibbon;
